// The Play-orientation quiz panel: presents ONE comprehension check, grades it
// in the browser against the authored answer key (`options[].correct`), and
// calls `onDone(correct)` once it resolves. Nothing is written to SpacetimeDB —
// this is the author-facing preview of what the Unreal trainee runtime does
// (present → answer → grade → gate → tries → timer). Mirrors
// `MineZeroCheckWidget`: one check at a time, tries and an optional countdown,
// the beat holds (the play loop awaits `onDone`) until it resolves.
import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorCheck, correctOptionIds, gradeCheck, isWorldSelection, totalTries } from './model';
import { GatedButton } from '../GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';

type Feedback = { correct: boolean; final: boolean; timeUp?: boolean };

export function QuizPanel({ check, onDone, sceneSelection, twinNames }: {
  check: EditorCheck;
  onDone: (correct: boolean) => void;
  /** The twin currently selected in the 3D scene — the trainee's live pick for a
   *  World-selection check. */
  sceneSelection?: string | null;
  twinNames?: Map<string, string>;
}) {
  // A World-selection check is answered IN the scene, not from an option list:
  // present a non-blocking corner prompt so the trainee can fly and pick a twin.
  if (isWorldSelection(check)) {
    return <WorldSelectionPanel check={check} onDone={onDone} sceneSelection={sceneSelection} twinNames={twinNames} />;
  }
  return <MultipleChoicePanel check={check} onDone={onDone} />;
}

function MultipleChoicePanel({ check, onDone }: { check: EditorCheck; onDone: (correct: boolean) => void }) {
  // Fix the option order once per check (shuffle only if authored), so a retry
  // keeps the same layout instead of re-shuffling under the trainee.
  const order = useMemo(() => {
    const opts = [...check.options];
    if (check.shuffleOptions) {
      for (let i = opts.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opts[i], opts[j]] = [opts[j], opts[i]];
      }
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.checkId]);

  const total = totalTries(check); // null = unlimited
  const correct = useMemo(() => new Set(correctOptionIds(check)), [check.checkId]);
  const [triesUsed, setTriesUsed] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null); // wall-clock ms — ONE budget for the whole check
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const expireRef = useRef<() => void>(() => {});
  const fbTimer = useRef<number | null>(null);

  const answering = feedback === null;
  const triesLeft = total == null ? Infinity : total - triesUsed;

  // The time limit is a SINGLE budget for the WHOLE check — one countdown that
  // spans every attempt. Arm it ONCE here (on check change) and NEVER re-arm it
  // between tries: an incorrect try only clears the pick, the clock keeps
  // running, and when it hits zero the check ENDS (see the countdown effect),
  // regardless of tries left. (Previously each incorrect answer restarted the
  // clock, so the limit effectively applied per answer, not per question.)
  useEffect(() => {
    setTriesUsed(0);
    setSelected(new Set());
    setFeedback(null);
    if (check.timeLimitSeconds != null) {
      setDeadline(Date.now() + check.timeLimitSeconds * 1000);
      setTimeLeft(check.timeLimitSeconds);
    } else {
      setDeadline(null);
      setTimeLeft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.checkId]);

  const submit = () => {
    if (!answering) return;
    const isCorrect = gradeCheck(check, [...selected]);
    const usedNext = triesUsed + 1;
    setTriesUsed(usedNext);
    const outOfTries = total != null && usedNext >= total;
    const final = isCorrect || outOfTries;
    setFeedback({ correct: isCorrect, final });
    if (final) setDeadline(null); // resolved → stop the clock
    // Brief feedback hold, then resolve (final) or start the next try. A non-final
    // retry does NOT re-arm the clock — the single budget keeps ticking.
    fbTimer.current = window.setTimeout(() => {
      fbTimer.current = null;
      if (final) onDone(isCorrect);
      else { setSelected(new Set()); setFeedback(null); }
    }, final ? 1500 : 1100);
  };

  // Time-up ends the WHOLE check as incorrect, even mid-feedback: cancel any
  // pending feedback hold so a wrong-try hold can't swallow the expiry. Kept in a
  // ref so the countdown effect always calls the latest `onDone`.
  expireRef.current = () => {
    if (fbTimer.current != null) { window.clearTimeout(fbTimer.current); fbTimer.current = null; }
    setTriesUsed((t) => t + 1);
    setDeadline(null);
    setFeedback({ correct: false, final: true, timeUp: true });
    fbTimer.current = window.setTimeout(() => { fbTimer.current = null; onDone(false); }, 1500);
  };

  // Drop a pending feedback hold when the check changes or the panel unmounts
  // (Stop mid-feedback), so it can't fire onDone after teardown.
  useEffect(() => () => { if (fbTimer.current != null) window.clearTimeout(fbTimer.current); }, [check.checkId]);

  // Countdown: one drift-free tick over the single budget; fires expiry once.
  useEffect(() => {
    if (deadline == null) return;
    const id = window.setInterval(() => {
      const left = Math.ceil((deadline - Date.now()) / 1000);
      setTimeLeft(Math.max(0, left));
      if (left <= 0) {
        window.clearInterval(id);
        expireRef.current(); // budget spent → end the check (incorrect)
      }
    }, 200);
    return () => window.clearInterval(id);
  }, [deadline]);

  const toggle = (id: string) => {
    if (!answering) return;
    setSelected((prev) => {
      if (check.multiSelect) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      return new Set([id]);
    });
  };

  // Reveal the key on the resolved feedback when authored, or on the last-try miss.
  const reveal = feedback != null && (check.showCorrectAnswer || (feedback.final && !feedback.correct));
  const timerPct =
    check.timeLimitSeconds != null && timeLeft != null
      ? Math.max(0, Math.min(100, (timeLeft / check.timeLimitSeconds) * 100))
      : null;

  return (
    <div className="oe-quiz-world">
      <div className="oe-quiz-card">
        <div className="oe-quiz-head">
          <span className="oe-quiz-kicker">Comprehension check</span>
          <span className="oe-quiz-tries">
            {total == null ? 'Unlimited tries' : `Try ${Math.min(triesUsed + (answering ? 1 : 0), total)} of ${total}`}
          </span>
        </div>
        {timerPct != null && (
          <div className="oe-quiz-timer">
            <div className="oe-quiz-timer-bar">
              <div style={{ width: `${timerPct}%` }} className={timeLeft != null && timeLeft <= 5 ? 'low' : ''} />
            </div>
            <span className="oe-quiz-timer-label">{timeLeft ?? 0}s left</span>
          </div>
        )}
        <div className="oe-quiz-question">{check.question || 'Untitled question'}</div>
        <div className="oe-quiz-opts">
          {order.map((o) => {
            const picked = selected.has(o.id);
            const isKey = correct.has(o.id);
            const showKey = reveal && isKey;
            const wrongPick = reveal && picked && !isKey;
            const cls = ['oe-quiz-opt'];
            if (picked) cls.push('picked');
            if (showKey) cls.push('key');
            if (wrongPick) cls.push('wrong');
            if (!answering) cls.push('locked');
            return (
              <button key={o.id} type="button" className={cls.join(' ')} onClick={() => toggle(o.id)} disabled={!answering}>
                <span className={'oe-quiz-mark ' + (check.multiSelect ? 'box' : 'dot') + (picked ? ' on' : '')} />
                <span className="oe-quiz-opt-text">{o.text}</span>
                {showKey && <span className="oe-quiz-key-tag">correct</span>}
              </button>
            );
          })}
        </div>
        <div className="oe-quiz-foot">
          {feedback ? (
            <span className={'oe-quiz-fb ' + (feedback.correct ? 'ok' : 'bad')}>
              {feedback.correct
                ? 'Correct!'
                : feedback.timeUp
                  ? "Time's up."
                  : feedback.final
                    ? 'Incorrect.'
                    : `Not quite — ${total == null ? 'try again' : `${triesLeft} tr${triesLeft === 1 ? 'y' : 'ies'} left`}.`}
            </span>
          ) : (
            <span className="oe-hint" style={{ margin: 0 }}>
              {check.multiSelect ? 'Select all that apply.' : check.grading === 'Any' ? 'Select one answer; several are correct.' : 'Select one answer.'} The beat holds until you answer.
            </span>
          )}
          <span className="oe-spacer" />
          {/* !answering = already graded: the feedback says so, plain disabled. */}
          <GatedButton
            className="oe-btn"
            busy={!answering}
            reason={selected.size === 0 ? 'Select an answer first' : null}
            onClick={() => submit()}
          >
            Submit
          </GatedButton>
        </div>
      </div>
    </div>
  );
}

/**
 * A World-selection check: the trainee flies the scene, selects a twin (or twins),
 * and commits with a button — no option list. Presented as a NON-blocking corner
 * card so the 3D scene stays interactive underneath (the wrapper ignores pointer
 * events; only the card catches clicks). Graded in the browser against the
 * expected twin set, exactly like the MCQ panel — this is the author-facing
 * preview of the Unreal/agent runtime, which grades the same set server-side.
 */
function WorldSelectionPanel({ check, onDone, sceneSelection, twinNames }: {
  check: EditorCheck;
  onDone: (correct: boolean) => void;
  sceneSelection?: string | null;
  twinNames?: Map<string, string>;
}) {
  const total = totalTries(check); // null = unlimited
  const [triesUsed, setTriesUsed] = useState(0);
  const [answer, setAnswer] = useState<string[]>([]); // twins the trainee committed
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const fbTimer = useRef<number | null>(null);
  const answering = feedback === null;
  const triesLeft = total == null ? Infinity : total - triesUsed;
  const label = (id: string) => twinNames?.get(id) ?? id;

  // Reset when the presented check changes; drop a pending feedback hold on
  // unmount (Stop mid-feedback) so it can't fire onDone after teardown.
  useEffect(() => { setTriesUsed(0); setAnswer([]); setFeedback(null); }, [check.checkId]);
  useEffect(() => () => { if (fbTimer.current != null) window.clearTimeout(fbTimer.current); }, [check.checkId]);

  const grade = (picks: string[]) => {
    if (!answering || picks.length === 0) return;
    const isCorrect = gradeCheck(check, picks);
    const usedNext = triesUsed + 1;
    setTriesUsed(usedNext);
    const outOfTries = total != null && usedNext >= total;
    const final = isCorrect || outOfTries;
    setFeedback({ correct: isCorrect, final });
    fbTimer.current = window.setTimeout(() => {
      fbTimer.current = null;
      if (final) onDone(isCorrect);
      else { setAnswer([]); setFeedback(null); } // next try: clear the committed set
    }, final ? 1500 : 1100);
  };

  const addSelected = () => {
    if (!answering || !sceneSelection) return;
    setAnswer((prev) => (prev.includes(sceneSelection) ? prev : [...prev, sceneSelection]));
  };
  const removeTwin = (id: string) => { if (answering) setAnswer((prev) => prev.filter((t) => t !== id)); };

  // Reveal the expected twins on a resolved miss (or when the author asked to
  // always show the answer).
  const reveal = feedback != null && (check.showCorrectAnswer || (feedback.final && !feedback.correct));
  const expected = correctOptionIds(check);
  const selLabel = sceneSelection ? label(sceneSelection) : null;
  const alreadyAdded = sceneSelection != null && answer.includes(sceneSelection);

  return (
    <div className="oe-quiz-world">
      <div className="oe-quiz-card">
        <div className="oe-quiz-head">
          <span className="oe-quiz-kicker">Select in the scene</span>
          <span className="oe-quiz-tries">
            {total == null ? 'Unlimited tries' : `Try ${Math.min(triesUsed + (answering ? 1 : 0), total)} of ${total}`}
          </span>
        </div>
        <div className="oe-quiz-question">{check.question || 'Select the correct twin in the scene'}</div>

        {/* Committed picks (multi-select shows the growing set). */}
        {answer.length > 0 && (
          <div className="oe-quiz-world-chips">
            {answer.map((id) => (
              <span key={id} className="oe-quiz-world-chip">
                {label(id)}
                {answering && <button type="button" title="Remove" aria-label="Remove" onClick={() => removeTwin(id)}><Icon of={Icons.action.remove} size={12} /></button>}
              </span>
            ))}
          </div>
        )}

        <div className="oe-quiz-world-sel">
          {selLabel ? <>Selected in scene: <strong>{selLabel}</strong></> : 'Click a twin in the 3D scene…'}
        </div>

        <div className="oe-quiz-foot">
          {feedback ? (
            <span className={'oe-quiz-fb ' + (feedback.correct ? 'ok' : 'bad')}>
              {feedback.correct ? 'Correct!' : feedback.final ? 'Incorrect.' : `Not quite — ${total == null ? 'try again' : `${triesLeft} tr${triesLeft === 1 ? 'y' : 'ies'} left`}.`}
              {reveal && <span className="oe-quiz-world-reveal"> Expected: {check.grading === 'Any' ? 'any of ' : ''}{expected.map(label).join(', ') || '-'}</span>}
            </span>
          ) : (
            <span className="oe-hint" style={{ margin: 0 }}>
              {check.multiSelect ? 'Fly to and select each twin, then Submit.'
                : check.grading === 'Any' ? 'Fly to and select any one matching twin, then Select.' : 'Fly to and select the twin, then Select.'}
            </span>
          )}
          <span className="oe-spacer" />
          {check.multiSelect ? (
            <>
              <GatedButton
                className="oe-btn ghost"
                busy={!answering}
                reason={!sceneSelection ? 'Select a twin in the scene first' : alreadyAdded ? 'This twin is already added' : null}
                onClick={addSelected}
              >
                + Add twin
              </GatedButton>
              <GatedButton
                className="oe-btn"
                busy={!answering}
                reason={answer.length === 0 ? 'Add at least one twin first' : null}
                onClick={() => grade(answer)}
              >
                Submit
              </GatedButton>
            </>
          ) : (
            <GatedButton
              className="oe-btn"
              busy={!answering}
              reason={sceneSelection ? null : 'Select a twin in the scene first'}
              onClick={() => grade(sceneSelection ? [sceneSelection] : [])}
            >
              Select
            </GatedButton>
          )}
        </div>
      </div>
    </div>
  );
}

/** One check's outcome from a quiz run, for the end-of-run summary. */
export type QuizOutcome = { check: EditorCheck; correct: boolean };

/**
 * End-of-run results, shown when the played orientation had any checks. Score is
 * the fraction answered correctly; pass/fail only when `passingScorePct` is set
 * (else the checks were informational). With `showAnswersAtEnd` the correct
 * option(s) of each question are revealed. Nothing persisted — preview only.
 */
export function QuizResults({
  outcomes, passingScorePct, showAnswersAtEnd, onClose,
}: {
  outcomes: QuizOutcome[];
  passingScorePct: number | null;
  showAnswersAtEnd: boolean;
  onClose: () => void;
}) {
  const total = outcomes.length;
  const got = outcomes.filter((o) => o.correct).length;
  const pct = total > 0 ? Math.round((got / total) * 100) : 0;
  const passed = passingScorePct == null ? null : pct >= passingScorePct;

  return (
    <div className="oe-quiz-backdrop">
      <div className="oe-quiz-card">
        <div className="oe-quiz-head">
          <span className="oe-quiz-kicker">Quiz results</span>
        </div>
        <div className="oe-quiz-score">
          <strong>{got}/{total} correct</strong>
          <span className="oe-quiz-pct">({pct}%)</span>
          {passed != null && <span className={'oe-quiz-verdict ' + (passed ? 'ok' : 'bad')}>{passed ? 'PASS' : 'FAIL'}</span>}
        </div>
        {passingScorePct != null && <div className="oe-hint" style={{ margin: '2px 0 0' }}>Passing score: {passingScorePct}%</div>}
        <div className="oe-quiz-opts" style={{ marginTop: 12 }}>
          {outcomes.map((o, i) => (
            <div key={o.check.checkId} className="oe-quiz-result-row">
              <span className={'oe-quiz-result-mark ' + (o.correct ? 'ok' : 'bad')}><Icon of={o.correct ? Icons.status.success : Icons.status.error} size={14} title={o.correct ? 'Correct' : 'Wrong'} /></span>
              <div className="oe-quiz-opt-text">
                <div>{i + 1}. {o.check.question || 'Untitled question'}</div>
                {showAnswersAtEnd && (
                  <div className="oe-quiz-answer">
                    Answer: {o.check.grading === 'Any' ? 'any of ' : ''}{correctOptionIds(o.check).map((id) => o.check.options.find((op) => op.id === id)?.text).filter(Boolean).join(', ') || '—'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="oe-quiz-foot">
          <span className="oe-spacer" />
          <button className="oe-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
